import { useWorkflow } from '../components/Workflow/WorkflowContext';
import { WorkflowHelper } from '../utils/workflow-helper';
import { useMemo, useCallback } from 'react';

/**
 * React hook that provides workflow helper functions integrated with the current workflow context
 * Must be used within a WorkflowProvider
 * 
 * @example
 * ```tsx
 * const { getLabelKey, getState, getDisplayName, getColor } = useWorkflowHelpers();
 * 
 * // Get label key for a state ID
 * const labelKey = getLabelKey(stateId);
 * 
 * // Get translated label using intl
 * const translatedLabel = intl.get(labelKey);
 * ```
 */
export const useWorkflowHelpers = () => {
  const { workflow } = useWorkflow();

  // Memoize the helper functions to prevent unnecessary re-renders
  const getLabelKey = useCallback((stateId: number | null | undefined): string | null => {
    return WorkflowHelper.getLabelKeyByStateId(workflow, stateId);
  }, [workflow]);

  const getState = useCallback((stateId: number | null | undefined) => {
    return WorkflowHelper.getStateById(workflow, stateId);
  }, [workflow]);

  const getDisplayName = useCallback((stateId: number | null | undefined): string | null => {
    return WorkflowHelper.getDisplayNameByStateId(workflow, stateId);
  }, [workflow]);

  const getColor = useCallback((stateId: number | null | undefined): string | null => {
    return WorkflowHelper.getColorByStateId(workflow, stateId);
  }, [workflow]);

  const hasState = useCallback((stateId: number | null | undefined): boolean => {
    return WorkflowHelper.hasState(workflow, stateId);
  }, [workflow]);

  const getAllStates = useCallback(() => {
    return WorkflowHelper.getAllStates(workflow);
  }, [workflow]);

  return useMemo(() => ({
    getLabelKey,
    getState,
    getDisplayName,
    getColor,
    hasState,
    getAllStates,
    workflow,
  }), [getLabelKey, getState, getDisplayName, getColor, hasState, getAllStates, workflow]);
};
